import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { WORK_CHAT_APPROVAL_MODE_QUERY_KEY } from '../../contracts';
import type { ApprovalMode } from '../../contracts/harness.types';
import { getWorkChatApprovalMode, setWorkChatApprovalMode } from '../api/settings';
import { useIsAuthenticated } from '../injected-auth';

export { WORK_CHAT_APPROVAL_MODE_QUERY_KEY };

/**
 * Seeds a new chat's composer with the mode the user last picked. `null` is a
 * real state — never picked — and each surface keeps its own fallback rather
 * than being moved onto a default nobody chose. Writes are best-effort: this
 * only remembers a preference, it never decides a live run's mode.
 *
 * API: GET/PATCH /api/v2/ml-scribe/settings (key `work-chat_approval_mode`)
 */
export function useWorkChatApprovalModePreference() {
  const isAuthenticated = useIsAuthenticated();
  const queryClient = useQueryClient();

  const { data } = useQuery<ApprovalMode | null, Error>({
    queryKey: [WORK_CHAT_APPROVAL_MODE_QUERY_KEY],
    enabled: isAuthenticated,
    queryFn: getWorkChatApprovalMode,
  });
  const {
    mutate,
    isPending,
    variables: inFlightApprovalMode,
  } = useMutation<ApprovalMode | null, Error, ApprovalMode>({
    mutationFn: setWorkChatApprovalMode,
    // The value sent, not the echo: a response that omits the key reads as
    // `null` and would blank the cache right after a good save.
    onSuccess: (_response, mode) => {
      queryClient.setQueryData([WORK_CHAT_APPROVAL_MODE_QUERY_KEY], mode);
    },
  });
  const persistedApprovalMode = data ?? null;

  const rememberApprovalMode = useCallback(
    (next: ApprovalMode) => {
      if (!isAuthenticated) return;
      // The cache only moves in `onSuccess`, so a pick made mid-write has to be
      // compared against the write in flight rather than the stale cache.
      if (next === (isPending ? inFlightApprovalMode : persistedApprovalMode)) return;
      mutate(next);
    },
    [inFlightApprovalMode, isAuthenticated, isPending, mutate, persistedApprovalMode]
  );

  return { persistedApprovalMode, rememberApprovalMode };
}
